import { useState, useEffect, useCallback } from 'react';
import { Copy, Plus, Trash2, ToggleLeft, ToggleRight, Eye, EyeOff, Key, Shield, Clock, ArrowRightLeft } from 'lucide-react';
import HelpPanel, { HelpSection, HelpCode } from '../components/HelpPanel';
import {
  fetchApiKeys,
  createApiKey,
  toggleApiKey,
  deleteApiKey,
} from '../api/integration.api';
import type { ApiKeyItem, ApiKeyCreated } from '../types/integration.types';
import { ENDPOINT_SCOPES, FIELD_SCOPES, SCOPE_LABELS } from '../types/integration.types';
import { fmtDate, fmtTime } from '@/lib/format-date';
import { bestaetige } from '@/lib/dialoge';

export default function ApiKeysPage() {
  const [keys, setKeys] = useState<ApiKeyItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Create-Dialog state
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newScopes, setNewScopes] = useState<string[]>([]);
  const [newExpires, setNewExpires] = useState('');
  const [creating, setCreating] = useState(false);

  // Anzeige des erstellten Keys (einmalig)
  const [createdKey, setCreatedKey] = useState<ApiKeyCreated | null>(null);
  const [showKey, setShowKey] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchApiKeys();
      setKeys(data);
      setError('');
    } catch {
      setError('Fehler beim Laden der API-Keys');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!newName.trim() || newScopes.length === 0) return;
    try {
      setCreating(true);
      const created = await createApiKey({
        name: newName.trim(),
        scopes: newScopes,
        ...(newExpires ? { expiresAt: new Date(newExpires).toISOString() } : {}),
      });
      setCreatedKey(created);
      setShowKey(true);
      setShowCreate(false);
      setNewName('');
      setNewScopes([]);
      setNewExpires('');
      load();
    } catch {
      setError('Fehler beim Erstellen des API-Keys');
    } finally {
      setCreating(false);
    }
  };

  const handleToggle = async (id: string) => {
    try {
      await toggleApiKey(id);
      load();
    } catch {
      setError('Fehler beim Ändern des API-Key-Status');
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!(await bestaetige(`API-Key "${name}" wirklich löschen?`))) return;
    try {
      await deleteApiKey(id);
      load();
    } catch {
      setError('Fehler beim Löschen des API-Keys');
    }
  };

  const handleCopy = () => {
    if (createdKey?.plaintextKey) {
      navigator.clipboard.writeText(createdKey.plaintextKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const toggleScope = (scope: string) => {
    setNewScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope],
    );
  };

  return (
    <div className="p-6 max-w-5xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">API-Keys</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Hier verwaltest du die API-Keys, mit denen du deine anderen Programme anbindest.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium text-white"
          style={{ backgroundColor: 'hsl(var(--primary))' }}
        >
          <Plus size={16} />
          Neuen Key erstellen
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* ── Hilfe-Panel ──────────────────────────────────────────────────── */}
      <HelpPanel title="So bindest du dein anderes Programm an">
        <HelpSection icon={<Key size={15} />} title="Authentifizierung">
          <p>
            Dein anderes Programm muss den API-Key als Bearer-Token im
            HTTP-Header jedes Requests mitsenden:
          </p>
          <p className="mt-1.5">
            <HelpCode>Authorization: Bearer sk_live_...</HelpCode>
          </p>
          <p className="mt-1.5">
            Der vollständige Key wird nur einmalig nach dem Erstellen angezeigt.
            Danach ist nur noch das Präfix sichtbar.
          </p>
        </HelpSection>

        <HelpSection icon={<Shield size={15} />} title="Berechtigungen (Scopes)">
          <p>Jeder API-Key hat festgelegte Berechtigungen. Dein anderes Programm kann nur Endpunkte aufrufen, die zu den zugewiesenen Scopes passen:</p>
          <ul className="mt-1.5 space-y-1 list-none pl-0">
            <li><HelpCode>time_entries:read</HelpCode> — Zeitbuchungen der Mitarbeiter abrufen</li>
            <li><HelpCode>payroll:read</HelpCode> — Tagesabrechnungen / Lohnverrechnungsdaten abrufen</li>
            <li><HelpCode>users:read</HelpCode> — Mitarbeiterliste mit externen IDs lesen</li>
            <li><HelpCode>selection_data:read</HelpCode> — Projekte und Tätigkeitsarten lesen</li>
            <li><HelpCode>time_master_data:write</HelpCode> — Stammdaten synchronisieren (Projekte, Positionen, Schritte, Tätigkeitsarten, Mitarbeiter)</li>
            <li><HelpCode>articles:read</HelpCode> — Artikel lesen (mit Änderungsdatum-Filter)</li>
            <li><HelpCode>articles:write</HelpCode> — Artikel über die Fremd-ID anlegen und ändern</li>
            <li><HelpCode>purchase:read</HelpCode> — zusätzlich Lieferanten und Einkaufspreise in den Artikeln</li>
            <li><HelpCode>site_docs:read</HelpCode> — freigegebene Baustellendokumente samt Inhalt und PDF abholen und als abgeholt vermerken</li>
          </ul>
        </HelpSection>

        <HelpSection icon={<ArrowRightLeft size={15} />} title="Basis-URL der API">
          <p>
            Alle Endpunkte befinden sich unter dem Pfad:
          </p>
          <p className="mt-1.5">
            <HelpCode>{window.location.origin}/api/v1/integration/api/</HelpCode>
          </p>
          <p className="mt-1.5">
            Die vollständige API-Dokumentation inkl. Beispiel-Payloads ist unter{' '}
            <HelpCode>{window.location.origin}/api/docs</HelpCode>{' '}
            (Swagger) abrufbar.
          </p>
        </HelpSection>

        <HelpSection icon={<Clock size={15} />} title="Rate-Limit">
          <p>
            Pro API-Key sind maximal 1.000 Requests pro Stunde erlaubt.
            Bei Überschreitung antwortet die API mit HTTP-Status <HelpCode>429</HelpCode>.
          </p>
        </HelpSection>
      </HelpPanel>

      {/* ── Einmalige Key-Anzeige ──────────────────────────────────────────── */}
      {createdKey && (
        <div
          className="mb-6 rounded-lg border-2 p-4"
          style={{ borderColor: 'hsl(var(--primary))', background: 'hsl(var(--card))' }}
        >
          <div className="flex items-center gap-2 mb-2">
            <Key size={18} className="text-primary" />
            <span className="font-semibold text-foreground">
              API-Key erstellt: {createdKey.name}
            </span>
          </div>
          <p className="text-sm text-muted-foreground mb-3">
            Kopiere den Key jetzt — er wird nur dieses eine Mal angezeigt.
          </p>
          <div className="flex items-center gap-2">
            <code
              className="flex-1 rounded-md border px-3 py-2 text-sm font-mono"
              style={{ background: 'hsl(var(--muted))' }}
            >
              {showKey ? createdKey.plaintextKey : '•'.repeat(48)}
            </code>
            <button
              onClick={() => setShowKey(!showKey)}
              className="rounded-md p-2 hover:bg-muted"
              title={showKey ? 'Verbergen' : 'Anzeigen'}
            >
              {showKey ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
            <button
              onClick={handleCopy}
              className="rounded-md p-2 hover:bg-muted"
              title="Kopieren"
            >
              <Copy size={16} />
            </button>
            {copied && <span className="text-xs text-green-600">Kopiert!</span>}
          </div>
          <button
            onClick={() => setCreatedKey(null)}
            className="mt-3 text-xs text-muted-foreground hover:underline"
          >
            Hinweis schließen
          </button>
        </div>
      )}

      {/* ── Create-Dialog (inline) ─────────────────────────────────────────── */}
      {showCreate && (
        <div
          className="mb-6 rounded-lg border p-5"
          style={{ background: 'hsl(var(--card))' }}
        >
          <h2 className="text-lg font-semibold text-foreground mb-4">Neuen API-Key erstellen</h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Name</label>
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="z.B. BMD Schnittstelle"
                className="w-full rounded-md border px-3 py-2 text-sm bg-background text-foreground"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Berechtigungen (Scopes)</label>
              <p className="mb-2 text-xs text-muted-foreground">
                Gib jedem Zugang nur, was er braucht. Ein Schlüssel liegt auf Dauer in einem
                fremden System — kommt er dort abhanden, ist der Schaden so groß wie sein Umfang.
              </p>
              <div className="grid grid-cols-2 gap-2">
                {ENDPOINT_SCOPES.map((scope) => (
                  <label
                    key={scope}
                    className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm cursor-pointer hover:bg-muted"
                  >
                    <input
                      type="checkbox"
                      checked={newScopes.includes(scope)}
                      onChange={() => toggleScope(scope)}
                      className="rounded"
                    />
                    <span className="text-foreground">{SCOPE_LABELS[scope]}</span>
                    <span className="text-xs text-muted-foreground ml-auto font-mono">{scope}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* AP-30-G: Felder sind keine Endpunkte, und die Maske sagt das —
                sonst wirkt „Stundensätze lesen" wie ein weiterer Datenbereich. */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Felder</label>
              <p className="mb-2 text-xs text-muted-foreground">
                Diese Häkchen öffnen keinen Bereich, sondern einzelne Felder in den Antworten.
                Ohne sie fehlen die Felder in der Nutzlast — sie sind nicht leer.
              </p>
              <div className="grid grid-cols-2 gap-2">
                {FIELD_SCOPES.map((scope) => (
                  <label
                    key={scope}
                    className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm cursor-pointer hover:bg-muted"
                  >
                    <input
                      type="checkbox"
                      checked={newScopes.includes(scope)}
                      onChange={() => toggleScope(scope)}
                      className="rounded"
                    />
                    <span className="text-foreground">{SCOPE_LABELS[scope]}</span>
                    <span className="text-xs text-muted-foreground ml-auto font-mono">{scope}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Ablaufdatum (optional)
              </label>
              <input
                type="date"
                value={newExpires}
                onChange={(e) => setNewExpires(e.target.value)}
                className="rounded-md border px-3 py-2 text-sm bg-background text-foreground"
              />
            </div>

            <div className="flex gap-2 pt-2">
              <button
                onClick={handleCreate}
                disabled={creating || !newName.trim() || newScopes.length === 0}
                className="rounded-md px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
                style={{ backgroundColor: 'hsl(var(--primary))' }}
              >
                {creating ? 'Erstelle...' : 'Key erstellen'}
              </button>
              <button
                onClick={() => setShowCreate(false)}
                className="rounded-md border px-4 py-2 text-sm text-foreground hover:bg-muted"
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Key-Liste ──────────────────────────────────────────────────────── */}
      {loading ? (
        <p className="text-muted-foreground text-sm">Lade API-Keys...</p>
      ) : keys.length === 0 ? (
        <div className="rounded-lg border border-dashed p-8 text-center text-muted-foreground">
          <Key size={32} className="mx-auto mb-3 opacity-40" />
          <p className="font-medium">Noch keine API-Keys vorhanden</p>
          <p className="text-sm mt-1">Erstelle einen Key, um deine anderen Programme anzubinden.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {keys.map((key) => (
            <div
              key={key.id}
              className="flex items-center gap-4 rounded-lg border p-4"
              style={{
                background: 'hsl(var(--card))',
                opacity: key.isActive ? 1 : 0.6,
              }}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-foreground">{key.name}</span>
                  {!key.isActive && (
                    <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-700">
                      Deaktiviert
                    </span>
                  )}
                  {key.expiresAt && new Date(key.expiresAt) < new Date() && (
                    <span className="rounded-full bg-yellow-100 px-2 py-0.5 text-xs text-yellow-700">
                      Abgelaufen
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                  <code className="font-mono">{key.keyPrefix}•••</code>
                  <span>Erstellt: {fmtDate(key.createdAt)}</span>
                  {key.lastUsedAt && (
                    <span>Zuletzt: {fmtDate(key.lastUsedAt)} {fmtTime(key.lastUsedAt)}</span>
                  )}
                  {key.expiresAt && (
                    <span>Ablauf: {fmtDate(key.expiresAt)}</span>
                  )}
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {(key.scopes as string[]).map((scope) => (
                    <span
                      key={scope}
                      className="rounded-full border px-2 py-0.5 text-xs font-mono text-muted-foreground"
                    >
                      {scope}
                    </span>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-1">
                <button
                  onClick={() => handleToggle(key.id)}
                  className="rounded-md p-2 hover:bg-muted"
                  title={key.isActive ? 'Deaktivieren' : 'Aktivieren'}
                >
                  {key.isActive ? (
                    <ToggleRight size={18} className="text-green-600" />
                  ) : (
                    <ToggleLeft size={18} className="text-muted-foreground" />
                  )}
                </button>
                <button
                  onClick={() => handleDelete(key.id, key.name)}
                  className="rounded-md p-2 hover:bg-red-50 text-red-500"
                  title="Löschen"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
